/**
 * Power Automate Environment API client
 */

import { requestWithRetry } from "../utils/http.js";
import type { AuthProvider } from "../auth/types.js";
import type { Environment, ApiListResponse } from "./types.js";
import { createChildLogger } from "../utils/logger.js";
import {
  EnvironmentNotFoundError,
  ApiError,
  AuthorizationError,
  wrapApiCall,
} from "../utils/errors.js";
import { FLOW_API_BASE, FLOW_API_VERSION } from "../utils/constants.js";
import { RESOURCES } from "../auth/types.js";
import { validateEnvironmentId } from "../utils/security.js";

const apiLogger = createChildLogger({ module: "env-api" });

export interface EnvironmentApiConfig {
  authProvider: AuthProvider;
}

/**
 * Client for Power Automate Environment REST API
 */
export class EnvironmentApi {
  private readonly authProvider: AuthProvider;

  constructor(config: EnvironmentApiConfig) {
    this.authProvider = config.authProvider;
  }

  /**
   * Get authorization header with bearer token
   */
  private async getAuthHeaders(): Promise<Record<string, string>> {
    const token = await this.authProvider.getToken();
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  }

  /**
   * Build API URL for environment operations
   */
  private buildUrl(path: string): string {
    const base = `${FLOW_API_BASE}/providers/Microsoft.ProcessSimple${path}`;
    const params = new URLSearchParams();
    params.set("api-version", FLOW_API_VERSION);
    return `${base}?${params.toString()}`;
  }

  /**
   * Handle API response errors
   */
  private async handleResponse<T>(
    response: Awaited<ReturnType<typeof requestWithRetry>>,
    context: string
  ): Promise<T> {
    const { statusCode, body } = response;

    // Always consume body to prevent connection pool leaks
    const responseText = await body.text();

    if (statusCode === 401) {
      throw new AuthorizationError(`${context}: Authentication failed`);
    }

    if (statusCode === 403) {
      throw new AuthorizationError(`${context}: Access denied`);
    }

    if (statusCode === 404) {
      throw new EnvironmentNotFoundError(context);
    }

    if (statusCode >= 400) {
      let errorDetails: unknown;
      try {
        errorDetails = JSON.parse(responseText);
      } catch {
        errorDetails = responseText;
      }
      throw new ApiError(`${context}: API error`, statusCode, errorDetails);
    }

    return JSON.parse(responseText) as T;
  }

  /**
   * List all environments accessible to the user
   */
  async listEnvironments(): Promise<Environment[]> {
    apiLogger.debug("Listing environments");

    const url = this.buildUrl("/environments");
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      const data = await this.handleResponse<ApiListResponse<Environment>>(
        response,
        "List environments"
      );
      apiLogger.info({ count: data.value.length }, "Listed environments");
      return data.value;
    }, "List environments");
  }

  /**
   * Get a specific environment by ID
   */
  async getEnvironment(
    environmentId: string,
    options?: { boundedRead: true; signal: AbortSignal },
  ): Promise<Environment> {
    if (options?.boundedRead) {
      // Opt-in discovery for experimental management reads. Existing callers keep
      // their historical behavior; this branch never invokes interactive auth.
      let body: Awaited<ReturnType<typeof requestWithRetry>>["body"] | undefined;
      try {
        const safeEnvId = validateEnvironmentId(environmentId);
        if (options.signal.aborted) throw new Error();
        const token = await this.authProvider.tryGetTokenForResourceSilent?.(RESOURCES.FLOW_SERVICE);
        if (typeof token !== "string" || !token.trim() || options.signal.aborted) throw new Error();
        const response = await requestWithRetry(this.buildUrl(`/environments/${safeEnvId}`), {
          method: "GET", headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
          signal: options.signal, headersTimeout: 10000, bodyTimeout: 10000,
        }, { maxAttempts: 1 });
        body = response.body;
        if (response.statusCode !== 200) throw new Error();
        const chunks: Buffer[] = [];
        let size = 0;
        for await (const chunk of body as AsyncIterable<Uint8Array>) {
          if (options.signal.aborted) throw new Error();
          size += chunk.length;
          if (size > 1024 * 1024) throw new Error();
          chunks.push(Buffer.from(chunk));
        }
        if (options.signal.aborted) throw new Error();
        return JSON.parse(Buffer.concat(chunks).toString("utf8")) as Environment;
      } catch {
        throw new ApiError("Copilot Studio environment discovery unavailable; no fallback was attempted.", 502);
      } finally {
        body?.destroy();
      }
    }
    apiLogger.debug({ environmentId }, "Getting environment");

    const safeEnvId = validateEnvironmentId(environmentId);
    const url = this.buildUrl(`/environments/${safeEnvId}`);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      const env = await this.handleResponse<Environment>(
        response,
        environmentId
      );
      apiLogger.info(
        { environmentId, displayName: env.properties.displayName },
        "Retrieved environment"
      );
      return env;
    }, `Get environment ${environmentId}`);
  }

  /**
   * Get the default environment for the user
   */
  async getDefaultEnvironment(): Promise<Environment | null> {
    const environments = await this.listEnvironments();
    const defaultEnv = environments.find((e) => e.properties.isDefault);

    if (defaultEnv) {
      apiLogger.info(
        { environmentId: defaultEnv.name, displayName: defaultEnv.properties.displayName },
        "Found default environment"
      );
    } else {
      apiLogger.warn("No default environment found");
    }

    return defaultEnv ?? null;
  }

  /**
   * Find environment by display name (case-insensitive partial match)
   */
  async findEnvironmentByName(name: string): Promise<Environment | null> {
    const environments = await this.listEnvironments();
    const lowerName = name.toLowerCase();

    const match = environments.find((e) =>
      e.properties.displayName.toLowerCase().includes(lowerName)
    );

    if (match) {
      apiLogger.debug(
        { searchName: name, matchedId: match.name, matchedName: match.properties.displayName },
        "Found environment by name"
      );
    }

    return match ?? null;
  }
}
